import {
    ChangeDetectionStrategy,
    Component,
    inject,
    input,
    model,
    output,
    ViewEncapsulation,
    TemplateRef,
    computed,
    Input,
} from '@angular/core';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { FiDialogsService } from '@fi-shared/fi-dialogs/fi-dialogs.service';
import { AppTextService } from '@fi-shared/app-text/app-text.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { ISection } from './models/sections-dnd-editor.models';
import { isDefined } from '@uirouter/core';
import { IContentLanguageLabelModel } from '../fi-content-language-shared/fi-content-language-shared';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { FiSectionsConfigService } from './services/fi-sections-config.service';
import { FiSectionsHelperService } from './services/fi-sections-helper.service';
import { FiTooltipIconClassType } from '@fi-shared/fi-icon-tooltip-button/fi-icon-tooltip-button.component';

@Component({
    selector: 'fi-sections-dnd-editor',
    templateUrl: './fi-sections-dnd-editor.component.html',
    styleUrls: ['./fi-sections-dnd-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-sections-dnd-editor' },
    standalone: false,
})
export class FiSectionsDndEditorComponent {
    private fiDialogsService = inject(FiDialogsService);
    private appTextService = inject(AppTextService);
    private saveChangesWarningService = inject(SaveChangesWarningService);
    private fiSectionsConfigService = inject(FiSectionsConfigService);
    private fiSectionsHelperService = inject(FiSectionsHelperService);

    public sections = model.required<ISection[]>();
    public objectType = input.required<ObjectTypes>();
    public sectionItemTemplate = input<TemplateRef<any>>();
    public sectionBottomContentTemplate = input<TemplateRef<any>>();
    public selectedLanguageId = input.required<number>();
    public dragItemClassName = input<string>();
    public getNewSectionInstanceFn = input.required<() => ISection>();
    public isCustomDeleteHandler = input<boolean>(false);
    public onAddSectionItem = output<number>();
    public onConfirmDeleteSection = output<number>();

    public isAddSectionButtonAvailable = input<boolean>(false);
    public isNewUI = input<boolean>(false);

    @Input() isDeleteSectionButtonDisabledFn: (section: ISection) => boolean = () => false;

    protected sectionsDndEditorConfig = computed(() => {
        return this.fiSectionsConfigService.getDndEditorConfig(this.objectType());
    });

    protected elementDrop($event: CdkDragDrop<ISection[]>): void {
        if ($event.previousIndex === $event.currentIndex) {
            return;
        }
        const sections = [...this.sections()];
        moveItemInArray(sections, $event.previousIndex, $event.currentIndex);
        this.sections.set(sections);
    }

    protected onSectionChange(sectionIndex: number, section: ISection): void {
        this.sections.update((sections) => sections.map((s, i) => (i === sectionIndex ? { ...section } : s)));
    }

    protected handleCrossSectionDrop($event: CdkDragDrop<any[]>): void {
        const config = this.sectionsDndEditorConfig();
        const itemsPropName = config.sectionItemsPropName;

        // Get source and destination container IDs to find section indices
        const sourceContainerId = $event.previousContainer.id;
        const destContainerId = $event.container.id;

        // Extract section indices from container IDs (format: "section-drop-list0", "section-drop-list1", etc.)
        const sourceSectionIndex = parseInt(sourceContainerId.replace('section-drop-list', ''));
        const destSectionIndex = parseInt(destContainerId.replace('section-drop-list', ''));

        this.sections.update((sections) => {
            const updatedSections = sections.map((s) => ({ ...s, [itemsPropName]: [...(s[itemsPropName] || [])] }));

            // Perform the transfer
            transferArrayItem(
                updatedSections[sourceSectionIndex][itemsPropName],
                updatedSections[destSectionIndex][itemsPropName],
                $event.previousIndex,
                $event.currentIndex,
            );

            return updatedSections;
        });
    }

    protected openConfirmDeleteSectionDialog(sectionIndex: number): void {
        if (this.isCustomDeleteHandler()) {
            this.onConfirmDeleteSection.emit(sectionIndex);
            return;
        }
        this.fiDialogsService
            .confirmSimpleDelete({
                headerText: this.appTextService.get(this.sectionsDndEditorConfig().deleteSectionDialogHeaderTextId),
                messageText: this.appTextService.get(this.sectionsDndEditorConfig().deleteSectionDialogMessageTextId),
                preventUppercaseButtonLabel: true,
                config: {
                    panelClass: ['fi-rounded-dialog-panel', 'fi-confirm-delete-dialog-panel'],
                    autoFocus: false,
                },
            })
            .onYesButtonClick(() => this.removeSection(sectionIndex));
    }

    protected openSectionEditor(sectionIndex?: number): void {
        const config = this.sectionsDndEditorConfig();
        const isNewSection = !isDefined(sectionIndex);
        const editableSection = isNewSection ? this.getNewSectionInstanceFn()() : this.sections()[sectionIndex];

        this.fiSectionsHelperService
            .openSectionEditor(this.objectType(), this.selectedLanguageId(), isNewSection, editableSection)
            .onYesButtonClick((sectionNames: IContentLanguageLabelModel[]) => {
                editableSection.Names = sectionNames;
                if (isNewSection) {
                    this.addNewSection(editableSection);
                } else {
                    this.updateSection(sectionIndex, editableSection);
                }
            })
            .afterClosed(() => {
                if (config.enableSaveChangesWarning !== false) {
                    this.saveChangesWarningService.closeDialogOnTransition = false;
                }
            });

        if (config.enableSaveChangesWarning !== false) {
            this.saveChangesWarningService.closeDialogOnTransition = true;
        }
    }

    private addNewSection(newSection: ISection): void {
        this.sections.update((sections) => [...sections, newSection]);
    }

    private updateSection(sectionIndex: number, updatedSection: ISection): void {
        this.sections.update((sections) => sections.map((s, i) => (i === sectionIndex ? updatedSection : s)));
    }

    private removeSection(sectionIndex: number) {
        this.sections.update((sections) => sections.filter((_, i) => i !== sectionIndex));
    }

    protected readonly FiTooltipIconClassType = FiTooltipIconClassType;
}
